import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Edit2, Plus, UserPlus, Users as UsersIcon, X, Shield, ShieldCheck, Mail, User } from 'lucide-react';
import { getBackendUrl } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Form States
  const [editingUser, setEditingUser] = useState(null); // null means creating
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor'); // 'admin' or 'editor'
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = getBackendUrl();
      const { data } = await axios.get(`${url}/api/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setName('');
    setEmail('');
    setRole('editor');
    setPassword('');
    setError('');
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setName(user.name);
    setEmail(user.email);
    setRole(user.role);
    setPassword('');
    setError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name || !email) {
      setError("Lütfen ad ve e-posta alanlarını doldurun.");
      return;
    }

    const payload = {
      name,
      email,
      role
    };

    if (editingUser) {
      payload.id = editingUser.id;
      if (password) {
        payload.password = password;
      }
    } else {
      if (!password) {
        setError("Yeni kullanıcılar için şifre zorunludur.");
        return;
      }
      payload.password = password;
    }

    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/users`, payload, { withCredentials: true });
      setSuccess(editingUser ? "Kullanıcı başarıyla güncellendi!" : "Yeni kullanıcı başarıyla oluşturuldu!");
      fetchUsers();
      setTimeout(() => {
        setModalOpen(false);
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Bir hata oluştu.");
    }
  };

  const handleDelete = async (userId) => {
    if (currentUser && currentUser.id === userId) {
      alert("Kendi hesabınızı silemezsiniz.");
      return;
    }

    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;

    try {
      const url = getBackendUrl();
      await axios.delete(`${url}/api/users/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Silme işlemi başarısız.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800">Kullanıcı Yönetimi</h2>
          <p className="text-xs font-semibold text-slate-400 mt-1">CMS sistemini yönetecek kullanıcıları oluşturup yetkilendirin.</p>
        </div>
        
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-lg shadow-blue-600/10 transition-all"
        >
          <UserPlus size={16} /> Yeni Kullanıcı Ekle
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl p-12 text-center text-slate-500 font-semibold text-sm border border-slate-200/80">Yükleniyor...</div>
      ) : users.length === 0 ? (
        <div className="bg-white p-12 rounded-3xl border border-slate-200/80 text-center text-slate-500 flex flex-col items-center justify-center gap-3">
          <UsersIcon size={40} className="text-slate-300" />
          <span className="font-bold text-sm">Hiç kullanıcı bulunmuyor.</span>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Ad Soyad</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">E-posta</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Rol / Yetki</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Kayıt Tarihi</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-400 right-align text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs text-slate-600">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-bold text-slate-800 text-sm">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500 font-medium">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                        u.role === 'admin' 
                          ? 'bg-blue-50 text-blue-600 border border-blue-100' 
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {u.role === 'admin' ? <ShieldCheck size={12} /> : <Shield size={12} />}
                        {u.role === 'admin' ? 'Admin' : 'Editör'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs font-bold text-slate-400">
                      {new Date(u.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(u)}
                          className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-xl transition-all"
                          title="Düzenle"
                        >
                          <Edit2 size={15} />
                        </button>
                        
                        <button
                          onClick={() => handleDelete(u.id)}
                          disabled={currentUser && currentUser.id === u.id}
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-xl transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title={currentUser && currentUser.id === u.id ? "Kendinizi silemezsiniz" : "Sil"}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-extrabold text-slate-800 text-sm flex items-center gap-2">
                <UserPlus size={16} className="text-blue-600" />
                {editingUser ? "Kullanıcı Düzenle" : "Yeni Kullanıcı Oluştur"}
              </h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-xl transition"
              >
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-bold text-red-600">
                  {error}
                </div>
              )}
              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs font-bold text-green-600">
                  {success}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Ad Soyad</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-slate-400" size={16} />
                  <input 
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Örn: Ahmet Yılmaz"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">E-posta Adresi</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-slate-400" size={16} />
                  <input 
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Örn: ahmet@isilkoop.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Rol / Yetki</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="editor">Editör (Sadece Haber & Galeri yetkisi)</option>
                  <option value="admin">Admin (Tam yetkili)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  Şifre {editingUser && <span className="text-slate-400 font-normal">(Değiştirmek istemiyorsanız boş bırakın)</span>}
                </label>
                <input 
                  type="password"
                  required={!editingUser}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={editingUser ? "Yeni şifre girin" : "En az 6 karakter girin"}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-xs font-bold shadow-lg shadow-blue-600/10 transition disabled:opacity-50"
              >
                {editingUser ? "Kullanıcıyı Güncelle" : "Kullanıcı Oluştur"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
